import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { dashboardStats } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Plus, UserPlus, Ruler, Users, FolderKanban, FileText,
  ArrowRight, MapPin, Sparkles, HelpCircle, ChevronDown
} from "lucide-react";

// v6 F13 §2 — Fully clickable stat card with routing.
// Ondersteunt zowel `value` (grote nummer, klanten/projecten/offertes)
// als een pure subtitle-mode (Help & FAQ-kaart, zonder cijfer). Zo blijven
// alle vier de dashboardkaarten visueel gelijkwaardig.
const StatCard = ({ icon: Icon, label, value, accent, testId, to, subtitle }) => {
  const hasValue = value !== undefined && value !== null && value !== "";
  return (
    <Link
      to={to}
      className="emma-card flex items-center justify-between hover:border-[#D96C4A] hover:shadow-md transition group"
      data-testid={testId}
    >
      <div className="min-w-0 pr-2">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259]">{label}</div>
        {hasValue && (
          <div className="text-3xl font-heading font-bold text-[#2C2C2A] mt-1">{value}</div>
        )}
        {subtitle && (
          <div
            className={`text-[11px] text-[#4F4F4D] leading-tight ${hasValue ? "mt-0.5 truncate max-w-[140px]" : "mt-2"}`}
          >
            {subtitle}
          </div>
        )}
      </div>
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accent} group-hover:scale-105 transition-transform flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </Link>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_clients: 0,
    active_projects: 0,
    total_projects: 0,
    total_quotes: 0,
    recent_clients: [],
    active_projects_list: [],
  });
  // v6 F16 §2 — Actieve projecten & Recente klanten standaard DICHT op
  // desktop én mobiel. De 4 overzichtskaarten bovenaan zijn het snelle
  // dashboard; de lijsten eronder zijn detail die pas op vraag opengaan.
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);

  useEffect(() => {
    dashboardStats().then(setStats).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* v6 F16.2 §1 — Welkomstkaart met sterkere visuele balans.
          Desktop: 2-koloms grid, links titel in 2 regels + intro + knoppen,
          rechts een grote Emma met de volledige zwarte kader + gouden ring
          (geen mask). Kaart iets hoger (min-h op lg) voor rustige compositie.
          Mobiel: titel + intro full-width bovenaan, daaronder 2-koloms met
          knoppen stacked links en Emma rechts (hoogte matcht knopstack). */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 emma-card relative overflow-hidden lg:min-h-[380px]" data-testid="dashboard-welcome-card">
          <div className="absolute -right-16 -top-16 w-64 h-64 bg-[#D96C4A]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),260px] xl:grid-cols-[minmax(0,1fr),320px] gap-6 lg:gap-8 xl:gap-10 items-center">
            {/* Tekst- + knoppen-kolom */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[#5D7259] uppercase tracking-[0.2em] text-xs font-bold">
                <Sparkles className="w-3.5 h-3.5" />
                Welkom terug
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] mt-3 leading-tight">
                Klaar om vandaag<br />
                <span className="text-[#D96C4A]">scherp</span> te meten?
              </h1>
              <p className="text-[#4F4F4D] mt-3 max-w-xl leading-snug text-sm sm:text-base">
                Voeg een klant of project toe en start meteen met opmeten.
              </p>

              {/* Knoppen + mobile Emma. Op mobiel: knoppen stacked links +
                  Emma rechts, allebei items-stretch zodat Emma-hoogte matcht.
                  Op desktop: enkel knoppen horizontaal onder de intro. */}
              <div className="mt-5 flex items-stretch gap-4 lg:gap-0">
                <div className="flex flex-col sm:flex-row lg:flex-row gap-3 flex-shrink-0">
                  <Button
                    onClick={() => navigate("/klanten?new=1")}
                    variant="outline"
                    className="h-12 lg:h-14 px-5 rounded-xl border-[#2C2C2A] text-[#2C2C2A] hover:bg-[#2C2C2A] hover:text-white font-medium gap-2 whitespace-nowrap"
                    data-testid="new-client-button"
                  >
                    <UserPlus className="w-4 h-4" /> Nieuwe klant
                  </Button>
                  <Button
                    onClick={() => navigate("/projecten?new=1")}
                    className="h-12 lg:h-14 px-5 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] text-white font-medium gap-2 whitespace-nowrap"
                    data-testid="new-project-button"
                  >
                    <Plus className="w-4 h-4" /> Nieuw project
                  </Button>
                </div>
                {/* Mobile Emma — zit rechts naast de knopstack. items-stretch
                    op de parent zorgt dat de hoogte overeenkomt met de
                    knopstack (bovenrand = bovenrand 'Nieuwe klant',
                    onderrand = onderrand 'Nieuw project'). */}
                <img
                  src="https://customer-assets.emergentagent.com/job_measure-estimate-2/artifacts/7isgemwx_image.png"
                  alt="Emma — jouw opmeetassistent"
                  className="lg:hidden flex-1 max-w-[120px] object-contain object-center pointer-events-none select-none rounded-xl"
                  data-testid="dashboard-emma-mobile"
                />
              </div>
            </div>

            {/* Desktop Emma — grote rechter visuele kolom met volledige
                zwarte kader + gouden ring behouden (geen mask). */}
            <div className="hidden lg:flex items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_measure-estimate-2/artifacts/7isgemwx_image.png"
                alt="Emma — jouw opmeetassistent"
                className="w-full max-w-[260px] xl:max-w-[320px] 2xl:max-w-[340px] h-auto object-contain pointer-events-none select-none rounded-2xl"
                data-testid="dashboard-emma"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={Users} label="Klanten" value={stats.total_clients} accent="bg-[#8A9A86]" testId="stat-clients" to="/klanten" />
          <StatCard icon={FolderKanban} label="Projecten" value={stats.total_projects} accent="bg-[#D4A373]" testId="stat-projects" to="/projecten" subtitle={`${stats.active_projects} actief`} />
          <StatCard icon={FileText} label="Offertes" value={stats.total_quotes} accent="bg-[#2C2C2A]" testId="stat-quotes" to="/offertes" />
          {/* v6 F13 §2 — FAQ-kaart rustiger: geen groot nummer, korte subtekst
              die aansluit bij de andere drie kaarten qua opbouw. */}
          <StatCard icon={HelpCircle} label="Help &amp; FAQ" subtitle="Handleiding en veelgestelde vragen" accent="bg-[#D96C4A]" testId="stat-faq" to="/help" />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active projects — collapsible (default dicht op desktop + mobiel) */}
        <Card className="emma-card !p-0 overflow-hidden" data-testid="active-projects-card">
          <div className="px-6 py-5 border-b border-[#E5E2DC] flex items-center gap-3">
            <button
              type="button"
              onClick={() => setProjectsOpen((v) => !v)}
              className="flex items-center gap-3 flex-1 min-w-0 text-left group"
              aria-expanded={projectsOpen}
              aria-controls="active-projects-list"
              data-testid="active-projects-toggle"
            >
              <ChevronDown
                className={`w-4 h-4 text-[#5D7259] flex-shrink-0 transition-transform ${projectsOpen ? "" : "-rotate-90"}`}
              />
              <div className="min-w-0">
                <h2 className="font-heading text-xl font-semibold text-[#2C2C2A]">
                  Actieve projecten
                  <span className="ml-2 text-sm font-normal text-[#4F4F4D]">
                    ({stats.active_projects_list.length})
                  </span>
                </h2>
                <p className="text-sm text-[#4F4F4D]">Wat staat er op de planning?</p>
              </div>
            </button>
            <Link
              to="/projecten"
              className="text-sm font-medium text-[#D96C4A] hover:underline flex items-center gap-1 flex-shrink-0"
              data-testid="view-all-projects"
              onClick={(e) => e.stopPropagation()}
            >
              Alles <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {projectsOpen && (
            <div className="divide-y divide-[#E5E2DC]" id="active-projects-list" data-testid="active-projects-list">
              {stats.active_projects_list.length === 0 && (
                <div className="px-6 py-10 text-center text-[#4F4F4D]">
                  Nog geen actieve projecten. Klik op <strong>Nieuw project</strong> om te starten.
                </div>
              )}
              {stats.active_projects_list.map((p) => (
                <Link
                  key={p.id}
                  to={`/projecten/${p.id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-[#F0EFEA] transition"
                  data-testid={`project-row-${p.id}`}
                >
                  <div>
                    <div className="font-semibold text-[#2C2C2A]">{p.name}</div>
                    <div className="text-sm text-[#4F4F4D] flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{p.client_name}</span>
                      {p.location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#D96C4A]" />
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* Recent clients — collapsible (default dicht op desktop + mobiel) */}
        <Card className="emma-card !p-0 overflow-hidden" data-testid="recent-clients-card">
          <div className="px-6 py-5 border-b border-[#E5E2DC] flex items-center gap-3">
            <button
              type="button"
              onClick={() => setClientsOpen((v) => !v)}
              className="flex items-center gap-3 flex-1 min-w-0 text-left group"
              aria-expanded={clientsOpen}
              aria-controls="recent-clients-list"
              data-testid="recent-clients-toggle"
            >
              <ChevronDown
                className={`w-4 h-4 text-[#5D7259] flex-shrink-0 transition-transform ${clientsOpen ? "" : "-rotate-90"}`}
              />
              <div className="min-w-0">
                <h2 className="font-heading text-xl font-semibold text-[#2C2C2A]">
                  Recente klanten
                  <span className="ml-2 text-sm font-normal text-[#4F4F4D]">
                    ({stats.recent_clients.length})
                  </span>
                </h2>
                <p className="text-sm text-[#4F4F4D]">Laatst toegevoegd</p>
              </div>
            </button>
            <Link
              to="/klanten"
              className="text-sm font-medium text-[#D96C4A] hover:underline flex items-center gap-1 flex-shrink-0"
              data-testid="view-all-clients"
              onClick={(e) => e.stopPropagation()}
            >
              Alles <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {clientsOpen && (
            <div className="divide-y divide-[#E5E2DC]" id="recent-clients-list" data-testid="recent-clients-list">
              {stats.recent_clients.length === 0 && (
                <div className="px-6 py-10 text-center text-[#4F4F4D]">
                  Nog geen klanten geregistreerd.
                </div>
              )}
              {stats.recent_clients.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-[#F0EFEA] transition"
                  data-testid={`client-row-${c.id}`}
                >
                  <div>
                    <div className="font-semibold text-[#2C2C2A]">{c.name}</div>
                    <div className="text-sm text-[#4F4F4D]">
                      {c.email || c.phone || c.address || "—"}
                    </div>
                  </div>
                  <Link
                    to={`/klanten?edit=${c.id}`}
                    className="text-xs font-semibold text-[#D96C4A] hover:underline"
                    data-testid={`open-client-${c.id}`}
                  >
                    Openen
                  </Link>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
